import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CalendarBlank, CaretLeft, CaretRight, Clock, MapPin, Phone, WarningCircle, CheckCircle, UsersThree, Info,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import AssignStaffBox from '../components/AssignStaffBox';
import RequestDetailDialog from '../components/RequestDetailDialog';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const STAFF_COLORS = ['#1565C0', '#00897B', '#8E24AA', '#EF6C00', '#2E7D32', '#C2185B', '#5E35B1', '#00838F'];
const UNASSIGNED_COLOR = '#90A4AE';

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hhmm = (iso) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
const mondayOf = (d) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
};

const statusBadge = (item) => {
  if (item.status === 'completed' || item.status === 'reviewed') {
    return { label: item.auto_completed ? 'Chiusa automaticamente' : 'Conclusa', cls: 'bg-emerald-100 text-emerald-800' };
  }
  if (item.status === 'cancelled') return { label: 'Annullata', cls: 'bg-muted text-foreground/60' };
  return { label: 'In programma', cls: 'bg-sky-100 text-sky-800' };
};

const ProAgendaPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [selected, setSelected] = useState(dayKey(new Date()));
  const [hidden, setHidden] = useState([]);
  const [detailReq, setDetailReq] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/professionals/me/agenda');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento agenda');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const isCenter = !!data?.is_center;
  const staff = data?.staff || [];

  const colorOf = useCallback((pid) => {
    if (!pid) return UNASSIGNED_COLOR;
    const i = staff.findIndex((s) => s.professional_id === pid);
    return i >= 0 ? STAFF_COLORS[i % STAFF_COLORS.length] : UNASSIGNED_COLOR;
  }, [staff]);

  const visible = useMemo(() => {
    const items = data?.items || [];
    if (!isCenter || hidden.length === 0) return items;
    return items.filter((it) => !hidden.includes(it.assigned_internal_professional_id || 'none'));
  }, [data, isCenter, hidden]);

  const byDay = useMemo(() => {
    const map = {};
    visible.forEach((it) => {
      const k = dayKey(new Date(it.appointment_at));
      (map[k] = map[k] || []).push(it);
    });
    return map;
  }, [visible]);

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = Array.from({ length: offset }, () => null);
    for (let i = 1; i <= days; i += 1) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }),
    [weekStart],
  );

  const rows = useMemo(() => {
    if (!isCenter) return [{ professional_id: null, name: 'Le mie prestazioni' }];
    return [...staff.map((s) => ({ ...s })), { professional_id: null, name: 'Non assegnate' }];
  }, [isCenter, staff]);

  const selectedItems = byDay[selected] || [];
  const todayKey = dayKey(new Date());

  const toggleFilter = (pid) => {
    const key = pid || 'none';
    setHidden((h) => (h.includes(key) ? h.filter((x) => x !== key) : [...h, key]));
  };

  const markCompleted = async (requestId) => {
    try {
      await api.post(`/api/professionals/me/requests/${requestId}/action?action=complete`);
      toast.success('Prestazione segnata come conclusa');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="pro-agenda-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-semibold text-4xl text-foreground mb-2">Agenda</h1>
          <p className="text-foreground/70">
            {isCenter
              ? 'Tutte le prestazioni del Centro, divise per professionista dell\u2019organico.'
              : 'Tutte le prestazioni con data fissata dai clienti.'}
          </p>
        </div>
        <div className="flex rounded-full border border-border overflow-hidden" data-testid="agenda-view-switch">
          <button
            onClick={() => { setCursor(new Date(new Date(selected).getFullYear(), new Date(selected).getMonth(), 1)); setView('month'); }}
            data-testid="agenda-view-month"
            className={`px-4 py-1.5 text-sm transition-colors ${view === 'month' ? 'bg-sky-600 text-white' : 'hover:bg-muted/60'}`}
          >
            Mese
          </button>
          <button
            onClick={() => { setWeekStart(mondayOf(new Date(selected))); setView('week'); }}
            data-testid="agenda-view-week"
            className={`px-4 py-1.5 text-sm transition-colors ${view === 'week' ? 'bg-sky-600 text-white' : 'hover:bg-muted/60'}`}
          >
            Settimana
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Card className="neumorphism rounded-2xl p-5" data-testid="agenda-stat-upcoming">
          <div className="text-xs uppercase tracking-wider text-foreground/60 mb-1">In programma</div>
          <div className="text-3xl font-heading font-semibold text-sky-700">{data?.upcoming_count ?? 0}</div>
        </Card>
        <Card className="neumorphism rounded-2xl p-5" data-testid="agenda-stat-toclose">
          <div className="text-xs uppercase tracking-wider text-foreground/60 mb-1">Da segnare conclusa</div>
          <div className={`text-3xl font-heading font-semibold ${(data?.to_close_count ?? 0) > 0 ? 'text-amber-600' : 'text-foreground/40'}`}>
            {data?.to_close_count ?? 0}
          </div>
        </Card>
        <Card className="neumorphism rounded-2xl p-5" data-testid="agenda-stat-next">
          <div className="text-xs uppercase tracking-wider text-foreground/60 mb-1">Prossima prestazione</div>
          {data?.next ? (
            <>
              <div className="text-lg font-heading font-semibold">
                {new Date(data.next.appointment_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} · {hhmm(data.next.appointment_at)}
              </div>
              <div className="text-sm text-foreground/60">
                {data.next.service_type || 'Prestazione'}
                {data.next.assigned_internal_name ? ` · ${data.next.assigned_internal_name}` : ''}
              </div>
            </>
          ) : (
            <div className="text-sm text-foreground/50 mt-2">Nessuna prestazione in programma</div>
          )}
        </Card>
      </div>

      {isCenter && (
        <Card className="neumorphism rounded-2xl p-4 mb-6" data-testid="agenda-staff-filter">
          <div className="flex items-center gap-2 mb-3 text-sm font-medium">
            <UsersThree size={18} weight="duotone" className="text-primary" /> Professionisti dell'organico
          </div>
          <div className="flex flex-wrap gap-2">
            {rows.map((r) => {
              const key = r.professional_id || 'none';
              const off = hidden.includes(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleFilter(r.professional_id)}
                  data-testid={`agenda-filter-${key}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-all
                    ${off ? 'opacity-40 border-border' : 'border-transparent bg-muted/60'}`}
                >
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colorOf(r.professional_id) }} />
                  {r.name}
                </button>
              );
            })}
            {rows.length === 1 && (
              <span className="text-xs text-foreground/60">Nessun professionista in organico.</span>
            )}
          </div>
        </Card>
      )}

      {view === 'month' ? (
        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="neumorphism rounded-2xl p-5 lg:col-span-3" data-testid="agenda-calendar">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 font-heading font-semibold text-lg">
                <CalendarBlank size={20} weight="duotone" />
                {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" className="rounded-full h-8 w-8" data-testid="agenda-prev"
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
                  <CaretLeft size={14} />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full h-8 w-8" data-testid="agenda-next"
                  onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
                  <CaretRight size={14} />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-xs text-foreground/50 text-center uppercase tracking-wider">{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {grid.map((d, i) => {
                if (!d) return <div key={`e${i}`} />;
                const k = dayKey(d);
                const items = byDay[k] || [];
                const isSel = k === selected;
                return (
                  <button
                    key={k}
                    onClick={() => setSelected(k)}
                    data-testid={`agenda-day-${k}`}
                    className={`aspect-square rounded-xl p-1 flex flex-col items-center justify-center transition-all
                      ${isSel ? 'bg-sky-600 text-white shadow-md' : items.length ? 'bg-sky-50 hover:bg-sky-100' : 'hover:bg-muted/60'}
                      ${k === todayKey && !isSel ? 'ring-1 ring-sky-400' : ''}`}
                  >
                    <span className={`text-sm font-medium ${isSel ? '' : 'text-foreground/80'}`}>{d.getDate()}</span>
                    {items.length > 0 && (
                      <span className="mt-1 flex gap-0.5 flex-wrap justify-center max-w-full">
                        {items.slice(0, 4).map((it) => (
                          <span
                            key={it.request_id}
                            className="h-1.5 w-1.5 rounded-full"
                            style={{ backgroundColor: isSel ? '#ffffff' : colorOf(it.assigned_internal_professional_id) }}
                          />
                        ))}
                        {items.length > 4 && <span className={`text-[9px] ${isSel ? '' : 'text-foreground/50'}`}>+{items.length - 4}</span>}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="neumorphism rounded-2xl p-5 lg:col-span-2" data-testid="agenda-day-detail">
            <div className="font-heading font-semibold text-lg mb-4">
              {new Date(selected).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            {loading ? (
              <div className="text-sm text-foreground/50">Caricamento…</div>
            ) : selectedItems.length === 0 ? (
              <div className="text-sm text-foreground/50" data-testid="agenda-empty-day">
                Nessuna prestazione in questa giornata.
              </div>
            ) : (
              <div className="space-y-3">
                {selectedItems.slice().sort((a, b) => a.appointment_at.localeCompare(b.appointment_at)).map((it) => {
                  const b = statusBadge(it);
                  const late = ['accepted', 'in_progress'].includes(it.status) && new Date(it.appointment_at) < new Date();
                  return (
                    <div
                      key={it.request_id}
                      className="p-3 rounded-xl border border-border/60 bg-card/60 border-l-4"
                      style={{ borderLeftColor: colorOf(it.assigned_internal_professional_id) }}
                      data-testid={`agenda-item-${it.request_id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 font-medium">
                          <Clock size={16} weight="duotone" /> {hhmm(it.appointment_at)}
                        </div>
                        <Badge className={`${b.cls} border-0 text-xs`}>{b.label}</Badge>
                      </div>
                      <div className="text-sm text-foreground/80">{it.service_type || 'Prestazione'}</div>
                      {isCenter && (
                        <div className="text-xs mt-1 flex flex-wrap items-center gap-1" data-testid={`agenda-operator-${it.request_id}`}>
                          <span className="text-foreground/50">Operatore: </span>
                          <strong>{it.assigned_internal_name || 'non assegnato'}</strong>
                          {it.internal_assignment_status === 'pending' && (
                            <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">In attesa di conferma</Badge>
                          )}
                          {it.internal_assignment_status === 'declined' && (
                            <Badge className="bg-red-100 text-red-800 border-0 text-[10px]">Rifiutata</Badge>
                          )}
                        </div>
                      )}
                      {(it.comune || it.region) && (
                        <div className="text-xs text-foreground/60 flex items-center gap-1 mt-1">
                          <MapPin size={12} /> {it.comune || it.region}
                        </div>
                      )}
                      {it.patient_name && (
                        <div className="text-xs text-foreground/70 mt-1">
                          {it.patient_name}
                          {it.contact_phone && (
                            <span className="inline-flex items-center gap-1 ml-2">
                              <Phone size={12} /> {it.contact_phone}
                            </span>
                          )}
                        </div>
                      )}
                      {late && (
                        <div className="text-xs text-amber-700 flex items-center gap-1 mt-2">
                          <WarningCircle size={12} /> Da segnare conclusa: dopo 24h i crediti vengono scalati
                        </div>
                      )}
                      {isCenter && ['accepted', 'in_progress'].includes(it.status) && (
                        <div className="mt-3">
                          <AssignStaffBox request={it} onAssigned={load} />
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        {['accepted', 'in_progress'].includes(it.status) && it.internal_assignment_status !== 'pending' && (
                          <Button size="sm" className="rounded-full h-8" onClick={() => markCompleted(it.request_id)} data-testid={`agenda-complete-${it.request_id}`}>
                            <CheckCircle size={14} className="mr-1" /> Segna conclusa
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setDetailReq(it)} data-testid={`detail-${it.request_id}`}>
                          <Info size={14} className="mr-1" /> Dettaglio Richiesta
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => navigate('/professional/richieste')} data-testid={`agenda-open-${it.request_id}`}>
                          Vai alla richiesta
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      ) : (
        <Card className="neumorphism rounded-2xl p-5 overflow-x-auto" data-testid="agenda-week">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 font-heading font-semibold text-lg">
              <CalendarBlank size={20} weight="duotone" />
              {weekDays[0].toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} – {weekDays[6].toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
            </div>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" className="rounded-full h-8 w-8" data-testid="agenda-week-prev"
                onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}>
                <CaretLeft size={14} />
              </Button>
              <Button variant="outline" size="icon" className="rounded-full h-8 w-8" data-testid="agenda-week-next"
                onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}>
                <CaretRight size={14} />
              </Button>
            </div>
          </div>

          <div className="min-w-[900px]">
            <div className="grid grid-cols-8 gap-1 mb-1">
              <div className="text-xs uppercase tracking-wider text-foreground/50 py-2">Professionista</div>
              {weekDays.map((d) => (
                <div key={dayKey(d)} className={`text-xs text-center py-2 rounded-lg ${dayKey(d) === todayKey ? 'bg-sky-50 text-sky-700 font-medium' : 'text-foreground/60'}`}>
                  {WEEKDAYS[(d.getDay() + 6) % 7]} {d.getDate()}
                </div>
              ))}
            </div>
            {rows.filter((r) => !hidden.includes(r.professional_id || 'none')).map((r) => (
              <div key={r.professional_id || 'none'} className="grid grid-cols-8 gap-1 border-t border-border/50" data-testid={`agenda-week-row-${r.professional_id || 'none'}`}>
                <div className="py-3 pr-2 text-sm flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: colorOf(r.professional_id) }} />
                  <span className="truncate">{r.name}</span>
                </div>
                {weekDays.map((d) => {
                  const items = (byDay[dayKey(d)] || []).filter(
                    (it) => (it.assigned_internal_professional_id || null) === (r.professional_id || null),
                  );
                  return (
                    <div key={dayKey(d)} className="py-2 px-1 space-y-1 min-h-[52px]">
                      {items.map((it) => (
                        <button
                          key={it.request_id}
                          onClick={() => { setSelected(dayKey(d)); setView('month'); }}
                          data-testid={`agenda-week-item-${it.request_id}`}
                          className="w-full text-left rounded-lg px-2 py-1 text-[11px] text-white truncate hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: colorOf(it.assigned_internal_professional_id) }}
                        >
                          {hhmm(it.appointment_at)} · {it.service_type || 'Prestazione'}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      )}

      {detailReq && (
        <RequestDetailDialog
          request={detailReq}
          open={!!detailReq}
          onOpenChange={(o) => { if (!o) setDetailReq(null); }}
        />
      )}
    </div>
  );
};

export default ProAgendaPage;
